/**
 * Renderer-console contract for a FAILED add-camera direct-link leg.
 *
 * The Authenticate Source dialog drives three legs — camera credential auth,
 * cloud link registration, and the cloud link agent + its callback. When one
 * fails, the reason has historically lived only in the dialog DOM plus a
 * `device_auth_invalid` analytics property, so a red HIL run named a downstream
 * symptom (the `toBeHidden` assertion timing out) instead of the leg that
 * actually broke. This writes ONE greppable line per failed leg to the renderer
 * console — the stream the HIL app-error watcher taps (see
 * studio/electron/e2e/helpers/app-error-watch.ts, signature
 * `direct-link-leg-failed`) — so the CI failure headline names the leg.
 *
 * The prefix is deliberately distinct from the `[API-403] auto-link-bridge-monitor`
 * poll lines: those are the Studio's OWN bridge auto-link, which self-heals, and
 * must never be conflated with this failure.
 *
 * ── WHY THE LINE CARRIES `code=` (ENG-4283 CI fix, 2026-09-04) ───────────────
 * The status code of a coded API failure reached NO console stream. `ApiError`
 * only renders the `[API-<code>] - An API error has occurred.` default when the
 * server sent NO message (api/types.ts), and #1348 made the server's own message
 * win — so the agent-registry link lambda's 404, whose body is always
 * "agent request not found" (api/common.api.agent-registry/registry/
 * linking_requests/link/main.go), never produced an `[API-404]` token anywhere.
 * The HIL wrong-env doctor-swap gate (iris-enabled.fixture.ts) confirms the
 * fault by code AND reason, so with no code on the wire it could never fire and
 * every wrong-env camera burned the full auth budget and reddened the lane
 * instead of being swapped. Emitting the code here is the observable condition
 * the product owes that gate — the code is APPENDED after `outcome=failed` so
 * the watcher's existing `leg=` regex keeps matching unchanged.
 *
 * This lives in its own module (not hooks.ts) so the console contract is
 * importable by a lightweight unit test and by the dialog without pulling the
 * heavy bridge-hooks dependency graph. Per rule: think-in-systems
 * (ai.knowledge/global) — minimal coupling for a cross-cut concern.
 */

/** Stable prefix every failed-leg line carries; the HIL watcher matches on it. */
export const DIRECT_LINK_LOG_PREFIX = '[direct-link]';

/** The three legs of the direct add-camera link, in the order they run. */
export type DirectLinkLeg =
  | 'camera-credential-auth'
  | 'cloud-link-registration'
  | 'cloud-link-agent';

/**
 * Read the HTTP status off a coded API failure, if it carries one.
 *
 * `ApiError` (api/types.ts) stamps `code`; the protobuf error envelope thrown
 * straight out of `wrapApiCall` carries one too. Everything else — a device
 * credential rejection, a transport `TypeError`, a plain string — has none, and
 * gets `undefined` so the log line simply omits the field rather than inventing
 * a status. Exported so the dialog reads a code the same way at every call site.
 *
 * @param error - The thrown value, of unknown shape.
 * @returns The numeric status, or undefined when the value carries none.
 */
export const directLinkErrorCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined;
  const { code } = error as { code: unknown };
  // Integer statuses only: a non-finite or non-numeric `code` (some libraries
  // use a string error code) must not be written as if it were an HTTP status.
  return typeof code === 'number' && Number.isInteger(code) ? code : undefined;
};

/**
 * Write the CI-visible per-leg failure line to the renderer console.
 *
 * @param leg - Which of the three legs failed.
 * @param reason - The failure message shown in the dialog (also carried here).
 * @param code - HTTP status, when the failure was a coded API error.
 */
export const logDirectLinkLegFailure = (
  leg: DirectLinkLeg,
  reason: string,
  code?: number
): void => {
  // Appended AFTER `outcome=failed`, never between `leg=` and `outcome=`: the
  // watcher signature anchors on `leg=(\S+) outcome=failed`, so inserting a
  // field there would silently stop matching every leg line.
  const codeField = code === undefined ? '' : ` code=${code}`;
  // console.error so the line reaches the renderer console stream watchPage taps;
  // JSON.stringify keeps a reason with spaces/quotes on one greppable line.
  console.error(
    `${DIRECT_LINK_LOG_PREFIX} leg=${leg} outcome=failed${codeField} reason=${JSON.stringify(reason)}`
  );
};
